/**
 * 弹幕模式库：每个模式是纯函数，只调用 field.spawn，不持有状态。
 * 签名统一为 (field, x, y, t, aim, opts)：
 *   field 敌弹场；x,y 发射点；t 发射者年龄(秒)；aim 自机相对发射点的角度(弧度)；
 *   opts 各模式参数（均有默认值，数值为 Normal 基准形态）。
 *
 * 难度分支（本文件是难度差异的主要载体，见 docs/01-design-research.md「难度分级」）：
 * 东方 ECL 的惯例是同一发弹指令按难度取不同结构参数；对应到本库：
 *   - diffCount()：各模式弹数按难度索引缩放（0.9/1.9/3.4/5.5，与 difficulties.js 一致）
 *   - 封闭环（ring/flower）弹数超过可穿越上限时自动分层：速度错开的多圈，
 *     每圈各自留有可穿越缝隙，总弹量不变（高难度 = 更多圈，而不是一圈没缝的子弹）
 *   - spiral 臂数封顶（旋转墙保持可读的穿越通道）、wall 在高难收窄缺口、homing 追加追踪强度
 *   - 更复杂的逐难度组件（如终卡追加层）由 bosses.js 用 ctx.diffIdx 手写分支
 * gameScene 进入时注入难度索引，退出时归 1（Normal）。
 * node 可直接 import。
 */
import { TAU, randRange } from '../engine/math.js';
import { DIFFICULTIES } from '../content/difficulties.js';

/** 当前难度索引（0 Easy / 1 Normal / 2 Hard / 3 Lunatic） */
let DIFF_IDX = 1;
/** 自定义弹量系数（Extra 等独立参数；非 null 时覆盖索引曲线） */
let CUSTOM_DENSITY = null;

/**
 * 设置难度索引。gameScene 进入时按所选难度设置，退出时必须归 1。
 * @param {number} idx 0..3，越界归 1
 */
export function setPatternDifficulty(idx) {
  DIFF_IDX = idx >= 0 && idx <= 3 ? idx : 1;
  CUSTOM_DENSITY = null;
}

/**
 * 直接设置弹量系数（Extra 模式用，独立于 4 档曲线）。
 * @param {number} mul >0
 */
export function setPatternDensity(mul) {
  CUSTOM_DENSITY = mul > 0 ? mul : null;
}

/** 当前难度索引（bosses.js 难度分支用）。 */
export function patternDifficulty() {
  return DIFF_IDX;
}

/** 弹量结构曲线（唯一来源：content/difficulties.js 的 density 字段，勿再拷贝）。 */
const DENSITY = DIFFICULTIES.map((d) => d.density);

/**
 * 难度弹数：同一弹幕在不同难度下的弹量设计（最少 1 颗）。
 * bosses.js 的自定义弹循环（非经模式函数的齐射）也应使用本函数。
 * @param {number} n Normal 基准弹数
 * @returns {number}
 */
export function diffCount(n) {
  const d = CUSTOM_DENSITY !== null ? CUSTOM_DENSITY : DENSITY[DIFF_IDX];
  return Math.max(1, Math.round(n * d));
}

/** 旋速/追踪等次要参数的难度系数（辅助，0.85/1/1.1/1.2）。 */
function auxMul() {
  return [0.85, 1, 1.1, 1.2][DIFF_IDX];
}

/**
 * 封闭环单层弹数上限：保证参考半径 180px 处相邻弹边缘缝 ≥ 12px
 *（最大判定半径 3.0 的玩家 + 安全余量可穿越；环扩张后缝隙只会更宽）。
 * @param {number} rB 弹半径
 */
export function ringLayerCap(rB = 4) {
  return Math.max(8, Math.floor((TAU * 180) / (12 + 2 * rB)));
}

/**
 * 审计钩子（tools/ring-audit 用）：封闭环每层 / 螺旋每次生成时回调
 * ({kind:'ring'|'spiral', count, rB})；生产环境保持 null 零开销。
 */
let ringAuditHook = null;
export function setRingAuditHook(fn) {
  ringAuditHook = fn;
}

/**
 * 封闭环分层：弹数超过可穿越上限时拆成多圈（速度逐层 +30%、角度错开），
 * 总弹量不变，每圈各自可穿越。bosses.js 的手工环也应使用本函数。
 * @param {number} n Normal 基准弹数
 * @param {number} rB 弹半径
 * @returns {Array<{count:number, speedMul:number, offset:number}>}
 */
export function ringLayers(n, rB = 4) {
  const raw = diffCount(n);
  const cap = ringLayerCap(rB);
  if (raw <= cap) {
    if (ringAuditHook) ringAuditHook({ kind: 'ring', count: raw, rB });
    return [{ count: raw, speedMul: 1, offset: 0 }];
  }
  const L = Math.ceil(raw / cap);
  const per = Math.ceil(raw / L);
  const layers = [];
  for (let i = 0; i < L; i++) {
    const count = i === L - 1 ? raw - per * (L - 1) : per;
    layers.push({ count, speedMul: 1 + 0.3 * i, offset: i * 0.37 });
    if (ringAuditHook) ringAuditHook({ kind: 'ring', count, rB });
  }
  return layers;
}

/** spiral 旋臂数上限：臂间通道保持可读（R=140 处边缘缝 ≥ 40px）。 */
const SPIRAL_ARM_CAP = 18;

/**
 * 滞留环弹数上限：spore 弹悬停在 R≈46 处（不扩张），弹数按该半径可穿越封顶
 *（悬停期间边缘缝 ≥ 6px，滞留结束后散射进一步让开通道）。
 * @param {number} n Normal 基准弹数
 * @param {number} rB 弹半径
 */
export function holdRingCount(n, rB = 5, radius = 46) {
  // 滞留环可穿越上限按**实际环半径**算（16px 弹间距标准；此前固定 46 参考半径，
  // 大半径滞留环（菌环等）被白白砍掉 3-4 倍弹量）
  const cap = Math.max(6, Math.floor((TAU * radius) / (6 + 2 * rB)));
  const count = Math.min(diffCount(n), cap);
  if (ringAuditHook) ringAuditHook({ kind: 'holdRing', count, rB, radius });
  return count;
}

/**
 * 环形 n 弹。
 * opts: {n=12, speed=2, r, color, offset=0(起始角)}
 */
export function ring(field, x, y, t, aim, opts = {}) {
  const speed = opts.speed || 2;
  const offset = opts.offset || 0;
  const rB = opts.r !== undefined ? opts.r : 4;
  // gapAt：缺口环（生门朝 aim 或指定弧度，width 弹数宽——"钻眼走位"的通用参数，
  // 东方的缺口环；有缺口的环天然可穿越，不再分层）
  const gap = opts.gapAt || null;
  if (gap) {
    const raw = diffCount(opts.n || 12);
    // width 为弧度（默认 0.35rad）——生门角宽各难度一致，密度再高也留得出路
    const gw = gap.width !== undefined ? gap.width : 0.35;
    const gapDir = gap.dir === undefined || gap.dir === 'aim' ? aim : gap.dir;
    if (ringAuditHook) ringAuditHook({ kind: 'ring', count: raw, rB, gap: gw });
    for (let i = 0; i < raw; i++) {
      const a = offset + (i / raw) * TAU;
      let d = a - gapDir;
      while (d > Math.PI) d -= TAU;
      while (d < -Math.PI) d += TAU;
      if (Math.abs(d) < gw / 2) continue; // 生门：缺口内不生成
      field.spawn({ x, y, speed, angle: a, r: opts.r, color: opts.color, onUpdate: opts.onUpdate, scale: opts.scale, harmless: opts.harmless, ttl: opts.ttl });
    }
    return;
  }
  for (const layer of ringLayers(opts.n || 12, rB)) {
    for (let i = 0; i < layer.count; i++) {
      const a = offset + layer.offset + (i / layer.count) * TAU;
      field.spawn({ x, y, speed: speed * layer.speedMul, angle: a, r: opts.r, color: opts.color, onUpdate: opts.onUpdate, scale: opts.scale, harmless: opts.harmless, ttl: opts.ttl });
    }
  }
}

/**
 * 扇形（朝 aim 方向张开）。
 * opts: {n=5, arc=0.6(张角rad), speed=2.5, r, color}
 */
export function spread(field, x, y, t, aim, opts = {}) {
  const n = diffCount(opts.n || 5);
  const arc = opts.arc !== undefined ? opts.arc : 0.6;
  const speed = opts.speed || 2.5;
  for (let i = 0; i < n; i++) {
    const a = aim - arc / 2 + (n === 1 ? arc / 2 : (i / (n - 1)) * arc);
    field.spawn({ x, y, speed, angle: a, r: opts.r, color: opts.color, onUpdate: opts.onUpdate, scale: opts.scale, harmless: opts.harmless, ttl: opts.ttl });
  }
}

/**
 * 自机狙：单发或奇数连发正中自机。
 * opts: {n=1, speed=3, spread=0.12(连发间隔角), r, color}
 */
export function aimed(field, x, y, t, aim, opts = {}) {
  const n = diffCount(opts.n || 1);
  const speed = opts.speed || 3;
  const gap = opts.spread !== undefined ? opts.spread : 0.12;
  const jitter = opts.jitter || 0; // 微移引弹：狙弹小随机角，玩家可用小位移引弹再大斜线走（评审 Q2）
  for (let i = 0; i < n; i++) {
    const a = aim + (i - (n - 1) / 2) * gap + (jitter ? randRange(-jitter, jitter) : 0);
    field.spawn({ x, y, speed, angle: a, r: opts.r, color: opts.color, onUpdate: opts.onUpdate, scale: opts.scale, harmless: opts.harmless, ttl: opts.ttl });
  }
}

/**
 * 旋转发射：每次调用按 t 推进的角发一串（配合定时器反复调用）。
 * 难度差异：旋臂数 × 弹量曲线 + 旋速 × 辅助系数。
 * opts: {arms=2, speed=2, spin=3.5(rad/s), r, color}
 */
export function spiral(field, x, y, t, aim, opts = {}) {
  const arms = Math.min(diffCount(opts.arms || 2), SPIRAL_ARM_CAP);
  const speed = opts.speed || 2;
  const spin = (opts.spin !== undefined ? opts.spin : 3.5) * auxMul();
  if (ringAuditHook) ringAuditHook({ kind: 'spiral', count: arms, rB: opts.r !== undefined ? opts.r : 4 });
  for (let i = 0; i < arms; i++) {
    const a = t * spin + (i / arms) * TAU;
    field.spawn({ x, y, speed, angle: a, r: opts.r, color: opts.color, onUpdate: opts.onUpdate, scale: opts.scale, harmless: opts.harmless, ttl: opts.ttl });
  }
}

/**
 * 花瓣：弹速随正弦摆动，视觉上呈花瓣轮廓。
 * opts: {n=16, speed=2.2, petals=5, amp=1, phase=0, r, color}
 */
export function flower(field, x, y, t, aim, opts = {}) {
  const speed = opts.speed || 2.2;
  const petals = opts.petals || 5;
  const amp = opts.amp !== undefined ? opts.amp : 1;
  const phase = opts.phase || 0;
  const rB = opts.r !== undefined ? opts.r : 4;
  for (const layer of ringLayers(opts.n || 16, rB)) {
    for (let i = 0; i < layer.count; i++) {
      const a = (i / layer.count) * TAU + phase + layer.offset;
      const sp = speed * layer.speedMul + Math.sin(a * petals + t * 2) * amp;
      field.spawn({ x, y, speed: sp, angle: a, r: opts.r, color: opts.color, onUpdate: opts.onUpdate, scale: opts.scale, harmless: opts.harmless, ttl: opts.ttl });
    }
  }
}

/**
 * 弹幕墙（横向一排，留一个缺口）。
 * 难度差异：弹数 × 弹量曲线；缺口按**像素宽度**（Hard/Lunatic 略窄但始终可穿越），
 * 不随弹数缩放——否则高难度下槽位变窄、缺口被挤没。
 * opts: {n=24, speed=1.8, angle=PI/2(下落), gapIndex=0..1(缺口位置比例，缺省随机),
 *        gapPx=52(缺口像素宽，Hard/Lunatic 默认 44), r, color}
 */
export function wall(field, x, y, t, aim, opts = {}) {
  const n = diffCount(opts.n || 24);
  const speed = opts.speed || 1.8;
  const angle = opts.angle !== undefined ? opts.angle : Math.PI / 2;
  const gapPx = opts.gapPx !== undefined ? opts.gapPx : (DIFF_IDX >= 2 ? 44 : 52);
  const slot = 440 / (n - 1);
  const gapW = Math.max(1, Math.round(gapPx / slot));
  const gi = opts.gapIndex !== undefined ? opts.gapIndex : randRange(0.08, 0.85);
  const gap = Math.max(0, Math.min(n - 1 - gapW, Math.round(gi * (n - 1 - gapW))));
  if (ringAuditHook) ringAuditHook({ kind: 'wall', count: n, rB: opts.r !== undefined ? opts.r : 4, gapPx: gapW * slot });
  for (let i = 0; i < n; i++) {
    if (i >= gap && i < gap + gapW) continue;
    field.spawn({ x: x + (i / (n - 1) - 0.5) * 440, y, speed, angle, r: opts.r, color: opts.color, onUpdate: opts.onUpdate, scale: opts.scale, harmless: opts.harmless, ttl: opts.ttl });
  }
}

/**
 * 随机落弹：从顶部随机位置落下。
 * opts: {n=8, speedMin=1.5, speedMax=3, drift=0.4(横向摆动rad), r, color}
 */
export function rain(field, x, y, t, aim, opts = {}) {
  const n = diffCount(opts.n || 8);
  const sMin = opts.speedMin !== undefined ? opts.speedMin : 1.5;
  const sMax = opts.speedMax !== undefined ? opts.speedMax : 3;
  const drift = opts.drift !== undefined ? opts.drift : 0.4;
  const lean = opts.lean !== undefined ? opts.lean : 0; // 整波统一风向（风雨），与每颗 jitter 叠加
  for (let i = 0; i < n; i++) {
    const a = Math.PI / 2 + lean + randRange(-drift, drift);
    const x = randRange(opts.xMin !== undefined ? opts.xMin : 16, opts.xMax !== undefined ? opts.xMax : 464);
    field.spawn({ x, y: -8, speed: randRange(sMin, sMax), angle: a, r: opts.r, color: opts.color, onUpdate: opts.onUpdate, scale: opts.scale, harmless: opts.harmless, ttl: opts.ttl, grace: opts.grace });
  }
}

/**
 * 扇形激光（朝 aim 张开的激光束）。
 * opts: {n=5, arc=0.9, speed=1.5, r=3, color, homing=0}
 */
export function laserFan(field, x, y, t, aim, opts = {}) {
  const n = diffCount(opts.n || 5);
  const arc = opts.arc !== undefined ? opts.arc : 0.9;
  const speed = opts.speed || 1.5;
  for (let i = 0; i < n; i++) {
    const a = aim - arc / 2 + (n === 1 ? arc / 2 : (i / (n - 1)) * arc);
    field.spawn({ x, y, speed, angle: a, kind: 'laser', r: opts.r !== undefined ? opts.r : 3, color: opts.color, onUpdate: opts.onUpdate, scale: opts.scale, harmless: opts.harmless, ttl: opts.ttl });
  }
}

/**
 * 少量追踪弹。
 * 难度差异：弹数 × 弹量曲线 + 追踪强度 × 辅助系数（上限 1）。
 * opts: {n=3, speed=1.6, arc=0.8, homing=0.6, r, color}
 */
export function homing(field, x, y, t, aim, opts = {}) {
  const n = diffCount(opts.n || 3);
  const arc = opts.arc !== undefined ? opts.arc : 0.8;
  const speed = opts.speed || 1.6;
  const h = Math.min(1, (opts.homing !== undefined ? opts.homing : 0.6) * auxMul());
  for (let i = 0; i < n; i++) {
    const a = aim - arc / 2 + (n === 1 ? arc / 2 : (i / (n - 1)) * arc);
    field.spawn({ x, y, speed, angle: a, homing: h, r: opts.r, color: opts.color, onUpdate: opts.onUpdate, scale: opts.scale, harmless: opts.harmless, ttl: opts.ttl });
  }
}
